import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { db } from '@/lib/db';

// Re-export do tipo centralizado para compatibilidade
export type { ProductionStep } from "@/types/production";
import type { ProductionStep } from "@/types/production";

export const useProductionSteps = (setor: string) => {
  // Realtime agora é gerenciado centralmente no useRealtimeSync
  // Removido para evitar duplicação de subscriptions

  return useQuery({
    queryKey: ['production-steps', setor],
    queryFn: async () => {
      // Filtro temporal igual ao usado em useAllProductionSteps:
      // mantém todos os steps ativos + CONCLUIDOs dos últimos 7 dias.
      // Evita estourar o limite de 1000 linhas do Supabase em setores com
      // grande histórico (ex.: EXPEDICAO/IMPRESSAO), que fazia steps
      // recentes "sumirem" do board (ver pedido 503365).
      const cutoffDate = new Date();
      cutoffDate.setDate(cutoffDate.getDate() - 7);

      // Sprint 2 - Fase 3: Query sem join de orders (normalização)
      const { data, error } = await db
        .from('production_steps')
        .select('*')
        .eq('setor', setor)
        .or(`status.neq.CONCLUIDO,updated_at.gte.${cutoffDate.toISOString()}`)
        // Walter 2026-05-11: ocultar steps de pedido cancelado nos quadros
        // operacionais por setor (DTF/SILK/BORDADO/SUBLIMACAO/CORTE/COSTURA/
        // SAIDA_ESTOQUE/EXPEDICAO/COMPRAS).
        .is('cancelled_at', null)
        .order('data_programada', { ascending: true });

      if (error) {
        console.error('Error fetching production steps:', error);
        throw error;
      }

      return data as ProductionStep[];
    },
    staleTime: 30000, // 30s - permite refetch após invalidação
    refetchInterval: false, // Realtime ativo via useProductionStepsSync - polling redundante removido
    refetchOnWindowFocus: false, // Gerenciado por usePageVisibility (debounce 60s)
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
  });
};

export const useAllProductionSteps = (
  searchOrderIds?: Set<string>,
  searchTerm?: string
) => {
  // Query principal: filtro de 7 dias (inalterada)
  const mainQuery = useQuery({
    queryKey: ['all-production-steps'],
    queryFn: async () => {
      const seteDiasAtras = new Date();
      seteDiasAtras.setDate(seteDiasAtras.getDate() - 7);
      const cutoffDate = seteDiasAtras.toISOString();

      const { data, error } = await db
        .from('production_steps')
        .select('*')
        .or(`status.neq.CONCLUIDO,updated_at.gte.${cutoffDate}`)
        // Walter 2026-05-11: pular steps cancelados na listagem global.
        .is('cancelled_at', null)
        .order('data_programada', { ascending: true });

      if (error) {
        console.error('Error fetching all production steps:', error);
        throw error;
      }

      return data as ProductionStep[];
    },
    staleTime: 30000,
    refetchInterval: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
    placeholderData: (previousData) => previousData,
  });

  // IDs para query complementar (memo estável)
  const searchIds = useMemo(() =>
    searchOrderIds?.size ? Array.from(searchOrderIds).slice(0, 100) : [],
    [searchOrderIds]
  );

  const idsCount = searchIds.length;

  // Query complementar: steps dos pedidos buscados (sem filtro temporal)
  const complementQuery = useQuery({
    queryKey: ['production-steps-search', searchTerm || '', idsCount],
    queryFn: async () => {
      const { data, error } = await db
        .from('production_steps')
        .select('*')
        // Mesmo filtro da query principal: sem ele, a busca reinjetava steps
        // CANCELADOS em allSteps e eles voltavam a bloquear a Expedição
        // (getBlockingPrerequisites não filtra por conta própria).
        .is('cancelled_at', null)
        .in('order_id', searchIds);
      if (error) throw error;
      return data as ProductionStep[];
    },
    enabled: !!searchTerm && searchIds.length > 0,
    staleTime: 10000,
    refetchOnWindowFocus: false,
  });

  // Mesclar sem duplicatas
  const mergedData = useMemo(() => {
    const main = mainQuery.data || [];
    const complement = complementQuery.data || [];
    if (complement.length === 0) return main;
    const existingIds = new Set(main.map(s => s.id));
    return [...main, ...complement.filter(s => !existingIds.has(s.id))];
  }, [mainQuery.data, complementQuery.data]);

  return {
    data: mergedData,
    isLoading: mainQuery.isLoading || complementQuery.isLoading,
  };
};
